import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { studentsApi } from '@/lib/api';
import type { Role } from '@/lib/types';

interface PaletteItem {
    id: string;
    label: string;
    hint?: string;
    icon?: string;
    action: () => void;
    keywords?: string;
    group: string;
}

const ROLE_NAV: Record<Role, Array<{ label: string; href: string; icon?: string }>> = {
    estudiante: [
        { label: 'Dashboard', href: '/estudiante/dashboard', icon: '🎓' },
        { label: 'Portafolio', href: '/estudiante/portafolio', icon: '📁' },
        { label: 'Mi Ruta', href: '/estudiante/mi-ruta', icon: '🗺️' },
        { label: 'Bitácora', href: '/estudiante/bitacora', icon: '📔' },
        { label: 'Datos personales', href: '/estudiante/datos-personales', icon: '🔐' },
        { label: 'Mi Perfil', href: '/estudiante/perfil', icon: '👤' },
    ],
    profesor: [
        { label: 'Dashboard', href: '/profesor/dashboard', icon: '📊' },
        { label: 'Cursos', href: '/profesor/cursos', icon: '📚' },
        { label: 'Alumnos', href: '/profesor/alumnos', icon: '👥' },
        { label: 'En práctica', href: '/profesor/en-practica', icon: '🏭' },
        { label: 'Observaciones', href: '/profesor/observaciones', icon: '📝' },
        { label: 'Mi Perfil', href: '/profesor/perfil', icon: '👤' },
    ],
    maestro_guia: [
        { label: 'Dashboard', href: '/maestro-guia/dashboard', icon: '🏭' },
        { label: 'Asistencia', href: '/maestro-guia/asistencia', icon: '📅' },
        { label: 'Bitácora', href: '/maestro-guia/bitacora', icon: '📔' },
        { label: 'Avance', href: '/maestro-guia/avance', icon: '📈' },
        { label: 'Observación', href: '/maestro-guia/observacion', icon: '📝' },
        { label: 'Evaluación', href: '/maestro-guia/evaluacion', icon: '📋' },
        { label: 'Mi Perfil', href: '/maestro-guia/perfil', icon: '👤' },
    ],
    administrador: [
        { label: 'Dashboard', href: '/admin/dashboard', icon: '⚙️' },
        { label: 'Usuarios', href: '/admin/usuarios', icon: '👥' },
        { label: 'Invitaciones', href: '/admin/invitaciones', icon: '✉️' },
        { label: 'Cursos', href: '/admin/cursos', icon: '📚' },
        { label: 'Empresas', href: '/admin/empresas', icon: '🏢' },
        { label: 'Rúbricas', href: '/admin/rubricas', icon: '📋' },
        { label: 'Logros', href: '/admin/logros', icon: '🏆' },
        { label: 'Mi Perfil', href: '/admin/perfil', icon: '👤' },
    ],
    super_admin: [
        { label: 'Dashboard', href: '/super-admin/dashboard', icon: '🔱' },
        { label: 'Liceos', href: '/super-admin/liceos', icon: '🏫' },
        { label: 'Usuarios', href: '/super-admin/usuarios', icon: '👥' },
        { label: 'Errores', href: '/super-admin/errores', icon: '🐛' },
    ],
};

function norm(s: string): string {
    return s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

interface Props {
    open: boolean;
    onClose: () => void;
}

export function CommandPalette({ open, onClose }: Props) {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [query, setQuery] = useState('');
    const [activeIdx, setActiveIdx] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLUListElement>(null);

    const canSearchStudents = user && user.role !== 'estudiante';

    const { data: students = [] } = useQuery({
        queryKey: ['students'],
        queryFn: () => studentsApi.list(),
        enabled: open && !!canSearchStudents,
    });

    useEffect(() => {
        if (open) {
            setQuery('');
            setActiveIdx(0);
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [open]);

    const items = useMemo<PaletteItem[]>(() => {
        if (!user) return [];
        const navItems: PaletteItem[] = (ROLE_NAV[user.role] || []).map(n => ({
            id: `nav:${n.href}`,
            label: n.label,
            hint: n.href,
            icon: n.icon,
            action: () => navigate(n.href),
            keywords: n.label,
            group: 'Páginas',
        }));

        const studentItems: PaletteItem[] = canSearchStudents
            ? students.map(s => ({
                id: `student:${s.id}`,
                label: s.name,
                hint: `${s.curso || ''} · ${s.specialty || ''} · ${s.company || '—'}`,
                icon: '🎓',
                action: () => navigate(`/informe/${s.id}`),
                keywords: `${s.name} ${s.specialty} ${s.curso} ${s.company || ''} ${s.rut}`,
                group: 'Alumnos',
            }))
            : [];

        return [...navItems, ...studentItems];
    }, [user, students, canSearchStudents, navigate]);

    const filtered = useMemo<PaletteItem[]>(() => {
        const q = norm(query);
        if (!q) return items;
        return items.filter(it => norm(it.keywords || it.label).includes(q));
    }, [items, query]);

    useEffect(() => {
        setActiveIdx(0);
    }, [query]);

    useEffect(() => {
        if (!open) return;
        function onKey(e: KeyboardEvent) {
            if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
            if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, filtered.length - 1)); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
            else if (e.key === 'Enter') {
                e.preventDefault();
                const it = filtered[activeIdx];
                if (it) { it.action(); onClose(); }
            }
        }
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, onClose, filtered, activeIdx]);

    useEffect(() => {
        if (!open) return;
        const el = listRef.current?.querySelector(`[data-idx="${activeIdx}"]`) as HTMLElement | null;
        el?.scrollIntoView({ block: 'nearest' });
    }, [activeIdx, open]);

    if (!open) return null;

    let lastGroup = '';

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label="Búsqueda global"
            className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] p-4 bg-black/50"
            onClick={onClose}
        >
            <div
                className="w-full max-w-xl bg-white dark:bg-slate-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden"
                onClick={e => e.stopPropagation()}
            >
                <div className="border-b border-gray-200 dark:border-gray-700">
                    <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        placeholder="Buscar páginas o alumnos..."
                        aria-label="Buscar"
                        className="w-full px-4 py-3 bg-transparent text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none"
                    />
                </div>
                <ul ref={listRef} role="listbox" className="max-h-[50vh] overflow-y-auto py-1">
                    {filtered.length === 0 && (
                        <li className="px-4 py-6 text-center text-sm text-gray-500">Sin resultados.</li>
                    )}
                    {filtered.map((it, i) => {
                        const showHeader = it.group !== lastGroup;
                        lastGroup = it.group;
                        return (
                            <div key={it.id}>
                                {showHeader && (
                                    <li className="px-3 py-1 text-xs uppercase font-semibold text-gray-400">{it.group}</li>
                                )}
                                <li
                                    data-idx={i}
                                    role="option"
                                    aria-selected={i === activeIdx}
                                    onClick={() => { it.action(); onClose(); }}
                                    onMouseEnter={() => setActiveIdx(i)}
                                    className={
                                        'flex items-center gap-3 px-3 py-2 cursor-pointer text-sm ' +
                                        (i === activeIdx
                                            ? 'bg-brand-blue/10 text-brand-blue dark:bg-brand-blue/20'
                                            : 'text-gray-700 dark:text-gray-300')
                                    }
                                >
                                    {it.icon && <span aria-hidden="true">{it.icon}</span>}
                                    <span className="flex-1 truncate">
                                        <strong>{it.label}</strong>
                                        {it.hint && <span className="ml-2 text-xs text-gray-500">{it.hint}</span>}
                                    </span>
                                </li>
                            </div>
                        );
                    })}
                </ul>
                <footer className="border-t border-gray-200 dark:border-gray-700 px-3 py-2 text-xs text-gray-500 dark:text-gray-400 flex justify-between">
                    <span>↑↓ navegar · ↵ abrir · Esc cerrar</span>
                    <span>{filtered.length} resultado{filtered.length === 1 ? '' : 's'}</span>
                </footer>
            </div>
        </div>
    );
}

export function useCommandPaletteShortcut(setOpen: (v: boolean) => void) {
    useEffect(() => {
        function onKey(e: KeyboardEvent) {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                setOpen(true);
            }
        }
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [setOpen]);
}
